import mixpanel from 'mixpanel-browser';
import posthog from 'posthog-js';
import { useEffect } from 'react';

import { isProduction, mixpanelApiHost, mixpanelToken } from '@dashboard/services/env';

class FacebookPixel {
  constructor() {
    this.fbq = null;
    this.isInitialized = false;
  }

  init() {
    this.fbq = window.fbq;
    this.isInitialized = this.fbq && typeof this.fbq === 'function';
  }

  track(eventName, values = {}, eventId = '') {
    if (!this.isInitialized) {
      return;
    }

    this.fbq('track', eventName, values, { eventID: eventId });
  }
}

class GoogleTag {
  track(eventName, values = {}) {
    if (!window.gtag || typeof window.gtag !== 'function') {
      return;
    }

    window.gtag('event', eventName, values);
  }

  trackPageview() {
    if (!window.gtag || typeof window.gtag !== 'function') {
      return;
    }

    // Track in Google Analytics using the page_view event
    window.gtag('event', 'page_view', {
      page_location: window.location.href,
      page_path: window.location.pathname + window.location.search,
      page_title: document.title,
    });

    // For Google Ads, we still use config to keep conversion tracking working
    window.gtag('config', 'AW-16833295745', {
      page_path: window.location.pathname + window.location.search,
      enhanced_conversions: true,
      allow_enhanced_conversions: true,
    });
  }
}

class LinkedInInsight {
  track(values = {}) {
    if (!window.lintrk || typeof window.lintrk !== 'function') {
      return;
    }

    window.lintrk('track', values);
  }
}

const facebookPixel = new FacebookPixel();
export const googleTag = new GoogleTag();
const linkedinInsight = new LinkedInInsight();

function checkShouldTrack(...args) {
  if (!isProduction) {
    console.log('Would track: ', ...args);
    return false;
  }

  if (!mixpanelToken) {
    return false;
  }

  return true;
}

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_id',
  'utm_source_platform',
  'utm_campaign_id',
  'utm_creative_format',
  'utm_marketing_tactic',
];

// Replaces the library's first-touch stamp (skip_first_touch_marketing
// below): that one set_onces initial_utm_* for identified users too, so a
// seller opening a UTM-tagged URL — e.g. their own "Powered by" badge —
// gets stamped as campaign-acquired. Only anonymous visitors carry a
// genuine first touch; ID merge carries the stamp onto the profile at signup.
function setFirstTouch(mp) {
  const distinctId = mp.get_distinct_id();
  const deviceId = mp.get_property('$device_id');
  const isAnonymous = distinctId === '$device:' + deviceId || distinctId === deviceId;
  if (!isAnonymous) {
    return;
  }

  // Kept in the cookie, not on the profile: a returning seller on a new device is
  // anonymous too, and their login page must not become their first touch.
  mp.register_once({ initial_landing_page: window.location.pathname });

  // Like the library, absent keys are written as null alongside present ones:
  // set_once then locks the whole tuple, so a later visit from a different
  // campaign can't fill in the gaps and produce a mixed-campaign first touch.
  const params = new URLSearchParams(window.location.search);
  const firstTouch = {};
  let hasUtm = false;
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    firstTouch['initial_' + key] = value;
    if (value) {
      hasUtm = true;
    }
  }

  if (hasUtm) {
    mp.people.set_once(firstTouch);
  }
}

function setInitialLandingPage(mp) {
  const landingPage = mp.get_property('initial_landing_page');
  if (landingPage) {
    mp.people.set_once({ initial_landing_page: landingPage });
  }
}

function initTracking() {
  if (!checkShouldTrack('init')) {
    return;
  }

  mixpanel.init(mixpanelToken, {
    api_host: mixpanelApiHost || 'https://api-eu.mixpanel.com',
    // no trailing slashes: avoids Next.js 308s through the /api/ingest proxy
    api_routes: {
      track: 'track',
      engage: 'engage',
      groups: 'groups',
      record: 'record',
      flags: 'flags',
    },
    debug: process.env.NODE_ENV !== 'production',
    track_pageview: true,
    ip: true,
    skip_first_touch_marketing: true,
    loaded: mp => {
      try {
        setFirstTouch(mp);
      } catch (e) {
        // no-op
      }
    },
  });
  facebookPixel.init();
}

function useTracking() {
  useEffect(() => {
    initTracking();
  }, []);

  return null;
}

function trackEvent(eventName, values = {}) {
  if (!checkShouldTrack('trackEvent', eventName, values)) {
    return;
  }

  try {
    mixpanel.track(eventName, values);
    if (eventName === 'Signup') {
      setInitialLandingPage(mixpanel);
    }
  } catch (e) {
    // no-op
  }

  if (eventName === 'Signup') {
    const eventId = values.userId;

    facebookPixel.track('CompleteRegistration', {}, eventId);

    facebookPixel.track(
      'StartTrial',
      {
        value: values.predicted_ltv, // use full value for facebook pixel
        currency: values.currency,
      },
      eventId,
    );

    googleTag.track('sign_up', {
      method: 'email',
      currency: values.currency,
      transaction_id: eventId,
      ...(values.email && { user_data: { email_address: values.email } }),
    });

    googleTag.track('conversion', {
      send_to: 'AW-16833295745/0OjKCI-6_e4aEIHr3to-',
      value: values.predicted_ltv / 10, // use 10% of the value for google ads as we track full value on subscribe event
      currency: values.currency,
      transaction_id: eventId,
      ...(values.email && { user_data: { email_address: values.email } }),
    });

    linkedinInsight.track({
      conversion_id: 20555852,
    });
  }

  if (eventName === 'Subscribe') {
    const eventId = values.subscriptionId;

    // Send to Google Ads
    googleTag.track('conversion', {
      send_to: 'AW-16833295745/h9KqCKSLnJIaEIHr3to-',
      value: values.monthlyPrice,
      currency: values.currency,
      transaction_id: eventId,
      ...(values.email && { user_data: { email_address: values.email } }),
      ...(values.gclid && { gclid: values.gclid }),
    });

    // Also send as a purchase event for Google Analytics
    googleTag.track('purchase', {
      transaction_id: eventId,
      value: values.monthlyPrice,
      currency: values.currency,
      items: [
        {
          item_id: values.plan,
          item_name: values.planName || 'Subscription Plan',
          category: 'subscription',
          quantity: 1,
          price: values.monthlyPrice,
        },
      ],
    });

    facebookPixel.track(
      'Subscribe',
      {
        value: values.predicted_ltv,
        currency: values.currency,
        subscription_id: values.subscriptionId,
        plan_id: values.plan_id,
      },
      eventId,
    );

    linkedinInsight.track({
      conversion_id: 19718308,
    });
  }
}

function identifyUser(userId) {
  if (!checkShouldTrack('identifyUser', userId)) {
    return;
  }

  try {
    mixpanel.identify(userId);
  } catch (e) {
    // no-op
  }

  try {
    if (posthog.__loaded) {
      posthog.identify(userId);
    }
  } catch (e) {
    // no-op
  }
}

function updateUser(values = {}) {
  if (!checkShouldTrack('updateUser', values)) {
    return;
  }

  try {
    mixpanel.people.set(values);
  } catch (e) {
    // no-op
  }
}

function setUser(values = {}) {
  if (!checkShouldTrack('setUser', values)) {
    return;
  }

  try {
    mixpanel.people.set_once(values);
  } catch (e) {
    // no-op
  }
}

function logout() {
  if (!checkShouldTrack('logout')) {
    return;
  }

  try {
    mixpanel.reset();
  } catch (e) {
    // no-op
  }

  try {
    if (posthog.__loaded) {
      posthog.reset();
    }
  } catch (e) {
    // no-op
  }
}

function trackPageview() {
  if (!checkShouldTrack('trackPageview')) {
    return;
  }

  try {
    mixpanel.track_pageview();
    googleTag.trackPageview();
  } catch (e) {
    // no-op
  }
}

const tracking = {
  identifyUser,
  trackEvent,
  setUser,
  updateUser,
  logout,
  trackPageview,
};

export { useTracking, tracking, setFirstTouch, setInitialLandingPage };
